import { fundImageUrl } from "@/lib/api-client";
import { formatInceptionCanonical } from "@/lib/format-inception";
/**
 * Maps a raw DDB `Fund` record to the `FundDetail` shape consumed by
 * FundDetailPage and all fund sub-components.
 *
 * This is the SINGLE place where DDB field names are translated to the
 * legacy display-layer aliases (type ← fundType, slug ← id, etc.) and
 * where web-only computed values are assembled (image URL, fund facts,
 * document list, broker list, etc.).
 */
import type { Fund as DdbFund } from "@etfsite/db/types";
import { SHARED_BROKERS } from "./fund-brokers";
import type {
  FundDetail,
  FundDocument,
  FundFact,
  Reason,
  WhyInvestItem,
} from "./fund-detail-types";

// ─── Helpers ────────────────────────────────────────────────────────────────

const DEFAULT_PRICE = { current: "—", change: "—", changePct: "—", nav: "—", premium: "—" };

/**
 * Format a raw decimal expense-waiver string (e.g. "0.0010") to a percent
 * display string (e.g. "0.10%").  If the value already contains "%" it is
 * returned as-is.  Null / empty / non-numeric values fall back to "—".
 */
function formatExpenseWaiver(value: string | null | undefined): string {
  if (!value) return "—";
  if (value.includes("%")) return value;
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(2)}%`;
}

/**
 * Format an inception date stored as ISO or other recognisable string to the
 * canonical MM/DD/YYYY slash format (ported from Dane etf-website).
 * Placeholder strings ("[ ]", "TBD", "Pre-Launch") and anything
 * formatInceptionCanonical cannot parse are returned verbatim.
 * Null/undefined falls back to "—".
 */
function formatInceptionDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const formatted = formatInceptionCanonical(dateStr);
  // formatInceptionCanonical returns "" for null/undefined and returns the
  // trimmed input verbatim when it cannot recognise the format, so a non-empty
  // result is always safe to display.
  return formatted || dateStr;
}

const CATEGORY_LABELS: Record<DdbFund["fundType"], string> = {
  thematic: "Thematic ETF",
  leveraged: "2x Leveraged ETF",
  buffer: "Structured Buffer ETF",
  fixed: "Fixed Income ETF",
};

function buildThematicFacts(f: DdbFund): FundFact[] {
  return [
    { label: "Ticker", value: f.ticker, highlight: true },
    { label: "AUM", value: f.aum ?? "—" },
    { label: "Expense Ratio", value: f.expenseRatio },
    { label: "Base Currency", value: f.baseCurrency ?? "—" },
    { label: "Last Closing Price", value: "—" },
    { label: "NAV", value: "—" },
    { label: "Premium / Discount", value: "—" },
    { label: "30-Day Median Bid/Ask Spread", value: "—" },
    { label: "SEC 30-Day Yield", value: f.sec30DayYield ?? "—" },
    { label: "Distribution Frequency", value: f.distributionFrequency ?? "—" },
    { label: "Exchange", value: f.exchange ?? "—" },
    { label: "Management Style", value: f.managementStyle ?? "—" },
    { label: "CUSIP", value: f.cusip ?? "—" },
    { label: "Inception Date", value: formatInceptionDate(f.inceptionDate) },
    { label: "Adviser", value: f.adviser ?? "—" },
    { label: "Distributor", value: f.distributor ?? "—" },
    { label: "Custodian", value: f.custodian ?? "—" },
    { label: "Administrator", value: f.administrator ?? "—" },
    { label: "Transfer Agent", value: f.transferAgent ?? "—" },
    { label: "Auditor", value: f.auditor ?? "—" },
  ];
}

function buildLeveragedFacts(f: DdbFund): FundFact[] {
  return [
    { label: "Ticker", value: f.ticker, highlight: true },
    { label: "Leverage Factor", value: `${f.leverageFactor ?? 2}× Daily` },
    { label: "Underlying / Reference", value: f.underlyingTicker ?? "—" },
    { label: "Expense Ratio", value: f.expenseRatio },
    { label: "AUM", value: f.aum ?? "—" },
    { label: "Exchange", value: f.exchange ?? "—" },
    { label: "Inception Date", value: formatInceptionDate(f.inceptionDate) },
    { label: "NAV", value: "—" },
    { label: "Market Price", value: "—" },
    { label: "Premium / Discount", value: "—" },
    { label: "30-Day Median Bid/Ask Spread", value: "—" },
    { label: "CUSIP", value: f.cusip ?? "—" },
    { label: "Adviser", value: f.adviser ?? "—" },
    { label: "Distributor", value: f.distributor ?? "—" },
    { label: "Custodian", value: f.custodian ?? "—" },
    { label: "Administrator", value: f.administrator ?? "—" },
    { label: "Transfer Agent", value: f.transferAgent ?? "—" },
    { label: "Auditor", value: f.auditor ?? "—" },
  ];
}

function buildBufferFacts(f: DdbFund): FundFact[] {
  return [
    { label: "Ticker", value: f.ticker, highlight: true },
    { label: "AUM", value: f.aum ?? "—" },
    { label: "Gross Expense Ratio", value: f.expenseGross ?? "—" },
    {
      label: "Expense Waiver",
      value: formatExpenseWaiver(f.expenseRatioDiscount),
      tooltip:
        "Corgi Strategies, LLC (the “Adviser”) has contractually agreed to waive a portion of its management fee equal to 0.10% of the Fund’s average daily net assets. This agreement has no termination date and may not be terminated without the approval of the Board of Trustees, upon not less than thirty (30) days’ prior written notice to the Adviser; provided that the Board may not terminate the agreement with respect to any Fund prior to the one-year anniversary of the effective date of the agreement with respect to such Fund. Amounts waived are not subject to recoupment by the Adviser.",
    },
    { label: "Net Expense Ratio", value: f.expenseRatio, highlight: true },
    { label: "Base Currency", value: f.baseCurrency ?? "—" },
    { label: "NAV", value: "—" },
    { label: "Premium / Discount", value: "—" },
    { label: "Distribution Frequency", value: f.distributionFrequency ?? "—" },
    { label: "Exchange", value: f.exchange ?? "—" },
    { label: "Mgmt. Style", value: f.managementStyle ?? "—" },
    { label: "CUSIP", value: f.cusip ?? "—" },
    { label: "Inception Date", value: formatInceptionDate(f.inceptionDate) },
    { label: "Adviser", value: f.adviser ?? "—" },
    { label: "Distributor", value: f.distributor ?? "—" },
    { label: "Custodian", value: f.custodian ?? "—" },
    { label: "Administrator", value: f.administrator ?? "—" },
    { label: "Transfer Agent", value: f.transferAgent ?? "—" },
    { label: "Auditor", value: f.auditor ?? "—" },
  ];
}

function buildFixedFacts(f: DdbFund): FundFact[] {
  return [
    { label: "Ticker", value: f.ticker },
    { label: "Expense Ratio", value: f.expenseRatio },
    { label: "Base Currency", value: f.baseCurrency ?? "—" },
    { label: "30-Day Median Bid/Ask Spread", value: "—" },
    { label: "Distribution Frequency", value: f.distributionFrequency ?? "—" },
    { label: "Exchange", value: f.exchange ?? "—" },
    { label: "Management Style", value: f.managementStyle ?? "—" },
    { label: "Adviser", value: f.adviser ?? "—" },
    { label: "Distributor", value: f.distributor ?? "—" },
    { label: "Custodian", value: f.custodian ?? "—" },
    { label: "Administrator", value: f.administrator ?? "—" },
    { label: "Transfer Agent", value: f.transferAgent ?? "—" },
    { label: "Auditor", value: f.auditor ?? "—" },
    { label: "Effective Duration", value: f.effectiveDuration ?? "—" },
    { label: "Weighted Avg Maturity", value: f.weightedAvgMaturity ?? "—" },
    { label: "Effective Maturity", value: f.weightedAvgMaturity ?? "—" },
    { label: "Number of Holdings", value: f.holdingsCount != null ? String(f.holdingsCount) : "—" },
    { label: "CUSIP", value: f.cusip ?? "—" },
    { label: "Inception Date", value: formatInceptionDate(f.inceptionDate) },
  ];
}

const FUND_FACTS_BUILDERS: Record<DdbFund["fundType"], (f: DdbFund) => FundFact[]> = {
  thematic: buildThematicFacts,
  buffer: buildBufferFacts,
  fixed: buildFixedFacts,
  leveraged: buildLeveragedFacts,
};

/**
 * Build the ordered document list for the fund detail page.
 * URL presence is the toggle: documents with no URL are always included but
 * rendered as greyed-out/disabled buttons — no separate boolean flag needed.
 *
 * `isSingleStockLeveraged` drives the Daily Holdings label:
 *   true  → "Daily Holdings / Exposure" (canonical for single-stock 2x funds)
 *   false → "Daily Holdings"            (canonical for thematic/buffer/fixed)
 */
function buildDocuments(
  docs: DdbFund["documents"],
  isSingleStockLeveraged: boolean,
): FundDocument[] {
  const entries: FundDocument[] = [
    {
      name: "Prospectus",
      type: "PDF",
      date: docs.prospectusDate ?? "",
      ...(docs.prospectusUrl ? { href: docs.prospectusUrl } : {}),
    },
    {
      name: "Fact Sheet",
      type: "PDF",
      date: docs.factSheetDate ?? "",
      ...(docs.factSheetUrl ? { href: docs.factSheetUrl } : {}),
    },
    {
      name: "SAI",
      type: "PDF",
      date: docs.saiDate ?? "",
      ...(docs.saiUrl ? { href: docs.saiUrl } : {}),
    },
    {
      name: isSingleStockLeveraged ? "Daily Holdings / Exposure" : "Daily Holdings",
      type: "CSV",
      date: docs.dailyHoldingsDate ?? "",
      ...(docs.dailyHoldingsUrl ? { href: docs.dailyHoldingsUrl } : {}),
    },
    {
      name: "Annual Report",
      type: "PDF",
      date: docs.annualReportDate ?? "",
      ...(docs.annualReportUrl ? { href: docs.annualReportUrl } : {}),
    },
  ];
  // Only show Summary Prospectus if URL exists
  if (docs.summaryProspectusUrl) {
    entries.push({
      name: "Summary Prospectus",
      type: "PDF",
      date: docs.summaryProspectusDate ?? "",
      href: docs.summaryProspectusUrl,
    });
  }
  if (docs.otherUrls) {
    for (const other of docs.otherUrls) {
      entries.push({ name: other.title, type: "PDF", date: "", href: other.url });
    }
  }
  return entries;
}

function mapWhyInvest(items: DdbFund["whyInvest"]): WhyInvestItem[] {
  return items
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((w) => ({ title: w.title, desc: w.description }));
}

/**
 * Map DDB `Reason[]` → FundDetail `Reason[]`.
 *
 * DB shape: `{ order, statTop, statBottom, caption }`
 *   - `statTop`    → big stat display (e.g. "34% CAGR")
 *   - `statBottom` → stat label (e.g. "Through 2030")
 *   - `caption`    → body / description text
 *
 * FundDetail render shape: `{ stat, statLabel, badge, title, body, footnote, image? }`
 * Mapping: stat=statTop, statLabel=statBottom, body=caption, title/badge/footnote=""/""/null.
 * Sorted ascending by `order` (matching mapWhyInvest pattern).
 */
function mapReasons(items: DdbFund["reasons"]): Reason[] {
  return items
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((r) => ({
      stat: r.statTop ?? undefined,
      statLabel: r.statBottom ?? undefined,
      badge: "",
      title: r.title ?? "",
      body: r.caption,
      footnote: null,
    }));
}

function parseDisplayNumber(value: string | null | undefined): number | undefined {
  if (!value) return undefined;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
}

// ─── Main mapper ─────────────────────────────────────────────────────────────

/**
 * Convert a raw DDB `Fund` record into the `FundDetail` shape that all fund
 * detail sub-components (FundHero, FundDetailsTable, etc.) expect.
 *
 * All DDB fields pass through; this function adds/renames the small set of
 * display-layer aliases that differ between the DDB record and FundDetail:
 *   - `type`        ← `fundType`
 *   - `slug`        ← `id`
 *   - `image`       ← `fundImageUrl(imageKeys[0])`
 *   - `inception`   ← `inceptionDate ?? "—"`
 *   - `heroDesc`    ← `summary ?? description ?? ""`
 *   - `whyInvest`   ← remapped from DDB `{order, title, description}` to `{title, desc}`
 *   - `documents`   ← expanded from DDB `FundDocuments` URL map to `FundDocument[]`
 *   - `disclosures` ← raw DDB `string | null` passed as `string` (empty when null)
 *   - Buffer fields ← flattened from `bufferConfig` + `bufferScenarios`
 */
export function ddbToFundDetail(f: DdbFund): FundDetail {
  const bc = f.bufferConfig;
  const image = fundImageUrl(f.imageKeys[0]);

  // Compute leveragedSubtype ahead of buildDocuments so the Daily Holdings
  // label can be set correctly ("Daily Holdings / Exposure" for single-stock
  // 2x funds; "Daily Holdings" for all other types).
  //
  // Defensive logic: basket (affiliated/unaffiliated) 2x funds have a
  // placeholder/empty underlyingTicker and no single-issuer; only true
  // single-stock funds have a real ticker + issuer. We intentionally ignore
  // `category` because basket funds may have category="2x Single Stock" in
  // DDB pending a data-fixes-cuo pass.
  const ut = f.underlyingTicker?.trim();
  const isBasket = !ut || ut === "[ ]" || f.underlyingIssuer == null;
  const leveragedSubtype: "single-stock" | "index" | undefined =
    f.fundType === "leveraged" ? (isBasket ? "index" : "single-stock") : undefined;
  const isSingleStockLeveraged = leveragedSubtype === "single-stock";

  return {
    // ── Pass all DDB fields through first ──────────────────────────────────
    // This ensures every DDB field is available on FundDetail even if not
    // explicitly remapped below. TypeScript will catch any required mismatches.
    ...f,

    // ── Display-layer aliases (overrides spread) ───────────────────────────
    type: f.fundType,
    slug: f.id,
    image,
    fullName: f.fullName ?? f.name,
    theme: f.theme ?? f.summary ?? "",
    exchange: f.exchange ?? "—",
    holdingsCount: f.holdingsCountOverride ?? f.holdingsCount ?? 0,
    inception: f.inceptionDate ?? "—",
    heroDesc: f.summary ?? f.description ?? "",
    categoryLabel: f.siteCategory ?? CATEGORY_LABELS[f.fundType],
    cusip: f.cusip ?? "",
    dateLabel: f.dateLabel ?? "—",
    aumAsOf: f.aumAsOf ?? undefined,
    expenseGross: f.expenseGross ?? undefined,
    expenseRatioDiscount: f.expenseRatioDiscount ?? undefined,
    seriesSlug: f.seriesSlug ?? undefined,
    whiteLabelRedirectUrl: f.whiteLabelRedirectUrl ?? undefined,
    navAsOf: f.aumAsOf ?? undefined,

    // ── Web-only computed fields ───────────────────────────────────────────
    price: DEFAULT_PRICE,
    whyInvest: mapWhyInvest(f.whyInvest),
    reasons: mapReasons(f.reasons),
    sectors: f.sectors ?? [],
    holdings: [],
    fundFacts: FUND_FACTS_BUILDERS[f.fundType](f),
    documents: buildDocuments(f.documents, isSingleStockLeveraged),
    brokers: SHARED_BROKERS,
    // Pass the raw disclosures string — FundDisclosures handles HTML detection
    // and paragraph splitting itself so it can choose the right render path.
    disclosures: f.disclosures ?? "",

    // ── Buffer aliases (flattened from bufferConfig) ───────────────────────
    referenceTicker: bc?.referenceTicker ?? undefined,
    referenceName: bc?.referenceName ?? undefined,
    bufferLevel: parseDisplayNumber(bc?.bufferLevel),
    bufferStart: 0,
    seriesMonth: bc?.seriesMonth ?? undefined,
    outcomePeriodStart: bc?.outcomePeriodStart ?? undefined,
    outcomePeriodEnd: bc?.outcomePeriodEnd ?? undefined,
    refPriceAtPeriodStart: parseDisplayNumber(bc?.refPriceAtPeriodStart),
    fundNavAtPeriodStart: parseDisplayNumber(bc?.fundNavAtPeriodStart),
    startingCapGrossPct: parseDisplayNumber(bc?.startingCapGross),
    startingCapNetPct: parseDisplayNumber(bc?.startingCapNet),
    riskStripText: bc?.riskStripText ?? undefined,
    strategyCallout: bc?.strategyCallout ?? undefined,
    bufferScenarios: f.bufferScenarios.map((s) => ({
      label: s.scenarioLabel,
      refReturn: s.refReturnLabel,
      fundReturn: s.fundReturnLabel,
      outcome: s.outcome,
      kind: s.kind,
    })),

    // ── Leveraged aliases ──────────────────────────────────────────────────
    underlyingEtfTicker: f.underlyingTicker ?? undefined,
    // For ETF-wrapper leveraged funds (e.g. XQTM → CQTM), this drives the
    // risk banner to say "ETF" instead of "common stock" and enables the
    // underlying-ETF holdings section. Single-stock 2x funds leave it unset.
    underlyingEtfName: f.underlyingEtfName ?? undefined,
    leverageFactor: f.leverageFactor ?? undefined,
    underlyingTicker: f.underlyingTicker ?? undefined,
    adviser: f.adviser ?? undefined,
    distributor: f.distributor ?? undefined,
    custodian: f.custodian ?? undefined,
    administrator: f.administrator ?? undefined,
    transferAgent: f.transferAgent ?? undefined,
    auditor: f.auditor ?? undefined,
    baseCurrency: f.baseCurrency ?? undefined,
    distributionFrequency: f.distributionFrequency ?? undefined,
    underlyingIssuer: f.underlyingIssuer ?? undefined,
    underlyingListing: f.underlyingListing ?? undefined,
    underlyingIndustry: f.underlyingIndustry ?? undefined,
    underlyingHeadquarters: f.underlyingHeadquarters ?? undefined,
    aboutUnderlying: f.aboutUnderlying ?? undefined,
    leveragedSubtype,

    // ── Fixed-income fields ────────────────────────────────────────────────
    indexName: f.indexName ?? undefined,
    indexProvider: f.indexProvider ?? undefined,
    rebalanceFrequency: f.rebalanceFrequency ?? undefined,
    indexInceptionDate: f.indexInceptionDate ?? undefined,
    maturityBandLabel: f.maturityBandLabel ?? undefined,

    // ── Web display overrides ──────────────────────────────────────────────
    holdingsAsOf: f.holdingsAsOf ?? undefined,
    founders: f.founders ?? undefined,
    sectorLabel: f.sectorLabel ?? undefined,
    sectorTickLabel: f.sectorTickLabel ?? undefined,
    sectorCountLabel: f.sectorCountLabel ?? undefined,
    sectorFootnote: f.sectorFootnote ?? undefined,
    sectorLayout: f.sectorLayout ?? undefined,

    // ── Feature-flag aliases ───────────────────────────────────────────────
    // The admin form stores `hasLiveHoldings` + `twelvedataEnabled` (canonical
    // stored fields). The detail page gates the Holdings table on
    // `showHoldingsTable` and the Daily-NAV / Premium-Discount section on
    // `showDailyNav`, so bridge the stored flags to those display aliases here.
    canShowPerformance: f.showPerformanceChart,
    showHoldingsTable: f.hasLiveHoldings,
    showDailyNav: f.twelvedataEnabled,
  } as FundDetail;
}
